/**
 * Client for the group-shared-note-fanout Medplum Bot.
 */
import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier, QuestionnaireResponse } from '@medplum/fhirtypes';

export const GROUP_SHARED_NOTE_FANOUT_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'group-shared-note-fanout',
} as const;

export type GroupSharedNoteSyncAction = 'fanout' | 'merge-from-patient';

export type GroupSharedNoteFanoutResult = {
  created: number;
  updated: number;
  skippedNotAttended: number;
  patientQuestionnaireResponseIds: string[];
  sharedQuestionnaireResponseId?: string;
};

export type GroupSharedNoteFanoutRequest = {
  questionnaireResponseId: string;
  appointmentId?: string;
  action?: GroupSharedNoteSyncAction;
  /** Default true for fanout. Pass false for draft sync (skip unresolved attendance). */
  strict?: boolean;
};

function getBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_GROUP_SHARED_NOTE_FANOUT_BOT_ID?.trim();
  if (id) return id;
  return {
    system:
      import.meta.env.VITE_MEDPLUM_GROUP_SHARED_NOTE_FANOUT_BOT_IDENTIFIER_SYSTEM?.trim() ??
      GROUP_SHARED_NOTE_FANOUT_BOT_IDENTIFIER.system,
    value:
      import.meta.env.VITE_MEDPLUM_GROUP_SHARED_NOTE_FANOUT_BOT_IDENTIFIER_VALUE?.trim() ??
      GROUP_SHARED_NOTE_FANOUT_BOT_IDENTIFIER.value,
  };
}

function parseFanoutResult(result: unknown): GroupSharedNoteFanoutResult {
  if (!result || typeof result !== 'object') {
    throw new Error('Group shared-note fan-out bot returned an empty result');
  }
  const typed = result as GroupSharedNoteFanoutResult;
  if (typeof typed.created !== 'number' || !Array.isArray(typed.patientQuestionnaireResponseIds)) {
    if ((result as QuestionnaireResponse).resourceType === 'QuestionnaireResponse') {
      return {
        created: 0,
        updated: 0,
        skippedNotAttended: 0,
        patientQuestionnaireResponseIds: [],
      };
    }
    throw new Error('Group shared-note fan-out bot returned an unexpected payload');
  }
  return typed;
}

export async function requestGroupSharedNoteFanout(
  medplum: MedplumClient,
  questionnaireResponseId: string,
  appointmentId?: string,
  options?: { strict?: boolean },
): Promise<GroupSharedNoteFanoutResult> {
  const result = await medplum.executeBot(
    getBotTarget(),
    {
      questionnaireResponseId,
      appointmentId,
      action: 'fanout',
      strict: options?.strict,
    } satisfies GroupSharedNoteFanoutRequest,
    ContentType.JSON,
  );
  return parseFanoutResult(result);
}

/** Merge a patient fan-out copy back into the shared Group note, then soft fan-out. */
export async function requestGroupSharedNoteMergeFromPatient(
  medplum: MedplumClient,
  questionnaireResponseId: string,
  appointmentId?: string,
): Promise<GroupSharedNoteFanoutResult> {
  const result = await medplum.executeBot(
    getBotTarget(),
    {
      questionnaireResponseId,
      appointmentId,
      action: 'merge-from-patient',
    } satisfies GroupSharedNoteFanoutRequest,
    ContentType.JSON,
  );
  return parseFanoutResult(result);
}
